// src/components/ReportCardUploadModal.js
import React, { useState } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { auth } from '../firebase';

function ReportCardUploadModal({ isOpen, onClose, child, userId, onUploadSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Auto-generate title when component opens or file changes
  React.useEffect(() => {
    if (isOpen && child && !reportTitle) {
      const today = new Date().toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
      setReportTitle(`${child.name} Report Card - ${today}`);
    }
  }, [isOpen, child, reportTitle]);

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a PDF or image file (JPG, PNG)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

const handleUpload = async () => {
  if (!selectedFile) {
    setError('Please select a file');
    return;
  }

  if (!reportTitle.trim()) {
    setError('Please enter a report title');
    return;
  }

  setIsUploading(true);
  setError(null);

  try {
    // Get auth token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to upload');
    }
    const token = await user.getIdToken();

    const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL ||
      'https://us-central1-arabic-ai-kids.cloudfunctions.net';

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('userId', userId);
    formData.append('childId', child.id);
    formData.append('reportTitle', reportTitle.trim());
    formData.append('notes', notes.trim());
    formData.append('token', token);  // ✅ Send token as form field instead

    console.log('Uploading report card...', {
      userId,
      childId: child.id,
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      fileType: selectedFile.type
    });

    // Add this right before: const response = await fetch(...)
    console.log('🔍 FormData Debug:');
    console.log('- File name:', selectedFile.name);
    console.log('- File size:', selectedFile.size);
    console.log('- File type:', selectedFile.type);
    console.log('- userId:', userId);
    console.log('- childId:', child.id);
    console.log('- reportTitle:', reportTitle);
    console.log('- URL:', `${FUNCTIONS_BASE_URL}/uploadReportCard`);

    // Check what's actually in FormData
    for (let [key, value] of formData.entries()) {
    if (value instanceof File) {
        console.log(`- FormData[${key}]:`, value.name, value.size, 'bytes');
    } else {
        console.log(`- FormData[${key}]:`, value);
    }
    }

    // ✅ CRITICAL: Do NOT set Content-Type header with FormData!
    // The browser automatically sets it with the boundary parameter
    const response = await fetch(`${FUNCTIONS_BASE_URL}/uploadReportCard`, {
      method: 'POST',
      body: formData  // No headers! Let browser handle it
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', errorText);
      throw new Error(errorText || 'Upload failed');
    }

    const result = await response.json();
    console.log('Upload success:', result);
    
    // Success! Reset form and close modal
    setSelectedFile(null);
    setReportTitle('');
    setNotes('');
    
    if (onUploadSuccess) {
      onUploadSuccess(result.reportCard);
    }
    
    onClose();
  } catch (err) {
    console.error('Upload error:', err);
    setError(err.message || 'Failed to upload report card');
  } finally {
    setIsUploading(false);
  }
};

  const resetForm = () => {
    setSelectedFile(null);
    setReportTitle('');
    setNotes('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">
            Upload Report Card
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isUploading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            For: <span className="font-semibold text-gray-800">{child?.name}</span>
          </div>

          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Card File *
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText size={40} className="mx-auto text-green-600" />
                  <div className="text-sm font-medium text-gray-800">
                    {selectedFile.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={40} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag & drop or click to select
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, JPG, or PNG (max 5MB)
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileSelect(file);
                    }}
                    className="hidden"
                    id="file-upload"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className="mt-3 inline-block px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition-colors text-sm"
                  >
                    Browse Files
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Report Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Title *
            </label>
            <input
              type="text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              placeholder="e.g., Ahmad Report Card - Feb 20, 2026"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isUploading}
            />
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Great improvement in pronunciation!"
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              disabled={isUploading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !reportTitle.trim()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReportCardUploadModal;